import _ from "lodash";
import { computed } from "mobx";
import { useMemo } from "react";
import { useLeadStore, usePipedriveStore } from "..";
import ILead from "../interfaces/ILead";

export default function (ownerFilter: string, searchFilter: string, sortBy: string): ILead[] {
    const leadStore = useLeadStore();
    const pipedriveStore = usePipedriveStore();

    const ownerFilteredLeads = useMemo(() => computed(() => {
        switch (ownerFilter) {
            case "All Owners":
                return leadStore.leads;
            case "None":
                return leadStore.leads.filter(lc => !lc.modifiedByUser);
            default:
                return leadStore.leads.filter(lc => lc.modifiedByUser === ownerFilter);
        }
    }), [ownerFilter]).get();

    const activityIdsDueDates = useMemo(() => computed(() => _.mapValues(_.keyBy(pipedriveStore.activities, a => a.id), a => a.dueDate)), []).get();

    const sortedLeads = useMemo(() => {
        const getReminderDate = (lead: ILead) => lead.activityId ? activityIdsDueDates[lead.activityId] : undefined;
        const orderByReminderDate = (direction: "asc" | "desc") => _.orderBy(ownerFilteredLeads, [lead => !getReminderDate(lead), getReminderDate], ["asc", direction]);

        switch (sortBy) {
            case LeadsSortingType.LandSizeAsc:
                return _.orderBy(ownerFilteredLeads, lead => lead.landSize, "asc");
            case LeadsSortingType.LandSizeDesc:
                return _.orderBy(ownerFilteredLeads, lead => lead.landSize, "desc");
            case LeadsSortingType.BuildingSizeAsc:
                return _.orderBy(ownerFilteredLeads, lead => lead.buildingSize, "asc");
            case LeadsSortingType.BuildingSizeDesc:
                return _.orderBy(ownerFilteredLeads, lead => lead.buildingSize, "desc");
            case LeadsSortingType.LotCoverageAsc:
                return _.orderBy(ownerFilteredLeads, lead => lead.lotCoverage, "asc");
            case LeadsSortingType.LotCoverageDesc:
                return _.orderBy(ownerFilteredLeads, lead => lead.lotCoverage, "desc");
            case LeadsSortingType.AssessmentAsc:
                return _.orderBy(ownerFilteredLeads, lead => lead.totalAssessment, "asc");
            case LeadsSortingType.AssessmentDesc:
                return _.orderBy(ownerFilteredLeads, lead => lead.totalAssessment, "desc");
            case LeadsSortingType.ModifiedNewest:
                return _.orderBy(ownerFilteredLeads, lead => lead.modifiedDate, "desc");
            case LeadsSortingType.ModifiedOldest:
                return _.orderBy(ownerFilteredLeads, lead => lead.modifiedDate, "asc");
            case LeadsSortingType.ReminderDateNewest:
                return orderByReminderDate("desc");
            case LeadsSortingType.ReminderDateOldest:
                return orderByReminderDate("asc");
            default:
                return _.orderBy(ownerFilteredLeads, lead => lead.modifiedDate, "asc");
        }
    }, [ownerFilteredLeads, sortBy, activityIdsDueDates]);

    return useMemo(() => {
        if (!searchFilter)
            return sortedLeads;

        const searchFilterInLowerCase = searchFilter.toLowerCase();

        return sortedLeads.filter(l =>
            (l.address && l.address.toLowerCase().includes(searchFilterInLowerCase)) ||
            (l.ownerName && l.ownerName.toLowerCase().includes(searchFilterInLowerCase)) ||
            (l.ownerAddress && l.ownerAddress.toLowerCase().includes(searchFilterInLowerCase)) ||
            (l.phoneNumber && l.phoneNumber.toLowerCase().includes(searchFilterInLowerCase)) ||
            (l.pipedriveCompanyId && pipedriveStore.organizations.find(o => o.id === l.pipedriveCompanyId)?.name.toLowerCase().includes(searchFilterInLowerCase)) ||
            (l.notes.some(n => n.text.toLowerCase().includes(searchFilterInLowerCase)))
        );
    }, [sortedLeads, searchFilter]);
}

export class LeadsSortingType {
    static LandSizeAsc = "LandSizeAsc";
    static LandSizeDesc = "LandSizeDesc";
    static BuildingSizeAsc = "BuildingSizeAsc";
    static BuildingSizeDesc = "BuildingSizeDesc";
    static LotCoverageAsc = "LotCoverageAsc";
    static LotCoverageDesc = "LotCoverageDesc";
    static AssessmentAsc = "AssessmentAsc";
    static AssessmentDesc = "AssessmentDesc";
    static ModifiedNewest = "ModifiedNewest";
    static ModifiedOldest = "ModifiedOldest";
    static ReminderDateNewest = "ReminderDateNewest";
    static ReminderDateOldest = "ReminderDateOldest";
}